import type { ActionReducer, MetaReducer } from '@ngrx/store';
import { authActions } from './auth.actions';

const NGRX_INIT_ACTIONS = new Set(['@ngrx/store/init', '@ngrx/store/update-reducers']);

export interface StorageSyncOptions<TState> {
  featureKey: string;
  storageKey: string;
  shouldClearFromStorage: (state: TState) => boolean;
  serialize: (state: TState) => unknown;
  deserialize: (raw: unknown) => TState | null;
}

export function createStorageSyncMetaReducer<TState>(options: StorageSyncOptions<TState>): MetaReducer {
  const getFeatureState = (rootState: unknown): TState | undefined =>
    (rootState as Record<string, unknown> | undefined)?.[options.featureKey] as TState | undefined;

  const loadFromStorage = (): TState | null => {
    try {
      const raw = localStorage.getItem(options.storageKey);
      if (!raw) return null;
      return options.deserialize(JSON.parse(raw));
    } catch {
      return null;
    }
  };

  const saveToStorage = (featureState: TState): void => {
    try {
      if (options.shouldClearFromStorage(featureState)) {
        localStorage.removeItem(options.storageKey);
        return;
      }
      localStorage.setItem(options.storageKey, JSON.stringify(options.serialize(featureState)));
    } catch {
      /* quota / private mode */
    }
  };

  return (reducer: ActionReducer<unknown>): ActionReducer<unknown> => {
    return (state, action) => {
      const nextState = reducer(state, action);

      if (NGRX_INIT_ACTIONS.has(action.type)) {
        const storedState = loadFromStorage();
        if (storedState) {
          return { ...(nextState as Record<string, unknown>), [options.featureKey]: storedState };
        }
      }

      if (action.type === authActions.logout.type) {
        return nextState;
      }

      const featureState = getFeatureState(nextState);
      if (featureState !== undefined && featureState !== null) {
        saveToStorage(featureState);
      }

      return nextState;
    };
  };
}
